import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Settings, Coins, Plus, Users, Edit, MapPin, Clock, Trash2, TrendingUp } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { NestPointsDisplay } from "./NestPointsDisplay";

interface Company {
  id: string;
  name: string;
  headquarters_timezone?: string;
}

interface NestPointsPolicy {
  id: string;
  company_id: string;
  policy_name: string;
  attribution_model: 'uniform' | 'by_seniority' | 'custom';
  base_points_per_week: number;
  maximum_balance: number;
  monthly_points_credit: number;
}

interface SeniorityLevel {
  id: string;
  company_id: string;
  level_name: string;
  monthly_points_credit: number;
  sort_order: number;
}

interface TimezonePolicy {
  id: string;
  company_id: string;
  max_timezone_difference_hours: number;
  enforce_timezone_restrictions: boolean;
  company_headquarters_timezone: string;
}

interface DistancePricing {
  id: string;
  company_id: string;
  timezone_difference_hours: number;
  points_per_week_multiplier: number;
  distance_category: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  position: string | null;
  seniority_level_id?: string;
}

interface EmployeePoints {
  id: string;
  employee_id: string;
  month: number;
  year: number;
  total_points: number;
  used_points: number;
  employee_name: string;
}

interface GovernanceTabProps {
  company: Company | null;
}

const GovernanceTab = ({ company }: GovernanceTabProps) => {
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState<NestPointsPolicy | null>(null);
  const [seniorityLevels, setSeniorityLevels] = useState<SeniorityLevel[]>([]);
  const [timezonePolicy, setTimezonePolicy] = useState<TimezonePolicy | null>(null);
  const [distancePricing, setDistancePricing] = useState<DistancePricing[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeePoints, setEmployeePoints] = useState<EmployeePoints[]>([]);
  
  // Dialog states
  const [isPolicyDialogOpen, setIsPolicyDialogOpen] = useState(false);
  const [isAddPointsDialogOpen, setIsAddPointsDialogOpen] = useState(false);
  const [isSeniorityDialogOpen, setIsSeniorityDialogOpen] = useState(false);
  
  // Form states
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [pointsToAdd, setPointsToAdd] = useState("");
  const [bonusReason, setBonusReason] = useState("");
  
  // Policy form states
  const [editedPolicy, setEditedPolicy] = useState<Partial<NestPointsPolicy>>({});
  const [editedTimezone, setEditedTimezone] = useState<Partial<TimezonePolicy>>({});
  
  const { toast } = useToast();

  useEffect(() => {
    if (company) {
      fetchAllData();
    }
  }, [company]);

  const fetchAllData = async () => {
    if (!company) return;
    setLoading(true);
    
    try {
      // Fetch company with active policy
      const { data: companyData } = await supabase
        .from('companies')
        .select('*, company_nest_points_policy!active_policy_id(*)')
        .eq('id', company.id)
        .single();

      if (companyData?.company_nest_points_policy) {
        const policyData = companyData.company_nest_points_policy as NestPointsPolicy;
        setPolicy(policyData);
        setEditedPolicy(policyData);
      }

      // Fetch seniority levels
      const { data: seniorityData } = await supabase
        .from('company_seniority_levels')
        .select('*')
        .eq('company_id', company.id)
        .order('sort_order');
      
      setSeniorityLevels(seniorityData || []);

      // Fetch timezone policy
      const { data: tzData } = await supabase
        .from('company_timezone_policy')
        .select('*')
        .eq('company_id', company.id)
        .maybeSingle();
      
      setTimezonePolicy(tzData);
      setEditedTimezone(tzData || {
        company_id: company.id,
        enforce_timezone_restrictions: false,
        max_timezone_difference_hours: 4,
        company_headquarters_timezone: company.headquarters_timezone || 'UTC+1'
      });

      // Fetch distance pricing
      const { data: distanceData } = await supabase
        .from('company_distance_pricing')
        .select('*')
        .eq('company_id', company.id)
        .order('timezone_difference_hours');
      
      setDistancePricing(distanceData || []);

      // Fetch employees
      const { data: employeesData } = await supabase
        .from('employees')
        .select('*')
        .eq('company_id', company.id)
        .not('first_name', 'is', null);
      
      setEmployees(employeesData || []);

      // Fetch current month's employee points
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      const employeeIds = (employeesData || []).map(e => e.id);
      
      const { data: pointsData } = await supabase
        .from('employee_points')
        .select('*')
        .in('employee_id', employeeIds)
        .eq('month', currentMonth)
        .eq('year', currentYear);

      const nameById = new Map((employeesData || []).map(e => [e.id, `${e.first_name} ${e.last_name}`]));
      const formattedPoints = (pointsData || []).map(p => ({
        ...p,
        employee_name: nameById.get(p.employee_id) || '—'
      }));
      
      setEmployeePoints(formattedPoints);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les données"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePolicy = async () => {
    if (!company || !editedPolicy.id) return;

    try {
      const { error } = await supabase
        .from('company_nest_points_policy')
        .update({
          policy_name: editedPolicy.policy_name,
          attribution_model: editedPolicy.attribution_model,
          base_points_per_week: editedPolicy.base_points_per_week,
          maximum_balance: editedPolicy.maximum_balance,
          monthly_points_credit: editedPolicy.monthly_points_credit
        })
        .eq('id', editedPolicy.id);

      if (error) throw error;

      // Save timezone policy
      if (timezonePolicy?.id) {
        await supabase
          .from('company_timezone_policy')
          .update(editedTimezone)
          .eq('id', timezonePolicy.id);
      } else if (company?.id) {
        await supabase
          .from('company_timezone_policy')
          .insert([{ 
            ...editedTimezone, 
            company_id: company.id 
          }]);
      }

      toast({
        title: "Politique sauvegardée",
        description: "La politique Nest Points a été mise à jour"
      });

      setIsPolicyDialogOpen(false);
      fetchAllData();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de sauvegarder la politique"
      });
    }
  };

  const handleAddBonusPoints = async () => {
    if (!selectedEmployee || !pointsToAdd || parseInt(pointsToAdd) <= 0) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez sélectionner un employé et saisir un nombre de points valide"
      });
      return;
    }

    try {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();

      const existingPoints = employeePoints.find(p => p.employee_id === selectedEmployee);
      
      if (existingPoints) {
        const newTotal = existingPoints.total_points + parseInt(pointsToAdd);
        
        if (policy && newTotal > policy.maximum_balance) {
          toast({
            variant: "destructive",
            title: "Erreur",
            description: `Le total ne peut pas dépasser ${policy.maximum_balance} points`
          });
          return;
        }

        await supabase
          .from('employee_points')
          .update({ total_points: newTotal })
          .eq('id', existingPoints.id);
      } else {
        await supabase
          .from('employee_points')
          .insert({
            employee_id: selectedEmployee,
            month: currentMonth,
            year: currentYear,
            total_points: parseInt(pointsToAdd),
            used_points: 0,
            policy_id: policy?.id,
            credited_by_automation: false
          });
      }

      toast({
        title: "Points bonus ajoutés",
        description: `${pointsToAdd} Nest Points ajoutés${bonusReason ? ` - ${bonusReason}` : ''}`
      });

      setIsAddPointsDialogOpen(false);
      setSelectedEmployee("");
      setPointsToAdd("");
      setBonusReason("");
      fetchAllData();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'ajouter les points"
      });
    }
  };

  const handleSaveSeniorityLevel = async (level: Partial<SeniorityLevel>) => {
    if (!company) return;

    try {
      if (level.id) {
        await supabase
          .from('company_seniority_levels')
          .update(level)
          .eq('id', level.id);
      } else {
        await supabase
          .from('company_seniority_levels')
          .insert([{ 
            level_name: level.level_name!,
            monthly_points_credit: level.monthly_points_credit!,
            sort_order: level.sort_order!,
            company_id: company.id 
          }]);
      }

      toast({
        title: "Niveau sauvegardé",
        description: "Le niveau de séniorité a été mis à jour"
      });

      fetchAllData();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  const handleDeleteSeniorityLevel = async (levelId: string) => {
    try {
      await supabase
        .from('company_seniority_levels')
        .delete()
        .eq('id', levelId);

      toast({
        title: "Niveau supprimé",
        description: "Le niveau de séniorité a été supprimé"
      });

      fetchAllData();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  const handleSaveDistancePricing = async (tzDiff: number, multiplier: number, category: string) => {
    if (!company) return;

    try {
      const existing = distancePricing.find(d => d.timezone_difference_hours === tzDiff);

      if (existing) {
        await supabase
          .from('company_distance_pricing')
          .update({ points_per_week_multiplier: multiplier, distance_category: category })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('company_distance_pricing')
          .insert({
            company_id: company.id,
            timezone_difference_hours: tzDiff,
            points_per_week_multiplier: multiplier,
            distance_category: category
          });
      }

      toast({
        title: "Tarification sauvegardée",
        description: "La tarification par distance a été mise à jour"
      });

      fetchAllData();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="h-64 bg-muted rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground mb-4">Aucune politique Nest Points configurée</p>
            <Button onClick={fetchAllData}>Créer une politique</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const weeksAffordable = policy ? Math.floor(policy.maximum_balance / policy.base_points_per_week) : 0;

  return (
    <div className="space-y-6">
      <Tabs defaultValue="policy" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="policy">Politique</TabsTrigger>
          <TabsTrigger value="economics">Économie</TabsTrigger>
          <TabsTrigger value="employees">Employés</TabsTrigger>
          <TabsTrigger value="advanced">Avancé</TabsTrigger>
        </TabsList>

        <TabsContent value="policy" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Configuration de la Politique
                  </CardTitle>
                  <CardDescription>
                    Définissez le modèle d'attribution des Nest Points
                  </CardDescription>
                </div>
                <Dialog open={isPolicyDialogOpen} onOpenChange={setIsPolicyDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Edit className="h-4 w-4 mr-2" />
                      Modifier
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Modifier la politique Nest Points</DialogTitle>
                      <DialogDescription>
                        Configurez comment les points sont attribués aux employés
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="policyName">Nom de la politique</Label>
                        <Input
                          id="policyName"
                          value={editedPolicy.policy_name || ''}
                          onChange={(e) => setEditedPolicy({ ...editedPolicy, policy_name: e.target.value })}
                          placeholder="Politique standard"
                        />
                      </div>

                      <div>
                        <Label>Modèle d'attribution</Label>
                        <RadioGroup
                          value={editedPolicy.attribution_model || 'uniform'}
                          onValueChange={(value: 'uniform' | 'by_seniority') => 
                            setEditedPolicy({ ...editedPolicy, attribution_model: value })
                          }
                        >
                          <div className="flex items-center space-x-2 p-4 border rounded-lg">
                            <RadioGroupItem value="uniform" id="uniform" />
                            <Label htmlFor="uniform" className="flex-1 cursor-pointer">
                              <div className="font-medium">Uniforme</div>
                              <div className="text-sm text-muted-foreground">
                                Tous les employés reçoivent le même nombre de points par mois
                              </div>
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2 p-4 border rounded-lg">
                            <RadioGroupItem value="by_seniority" id="by_seniority" />
                            <Label htmlFor="by_seniority" className="flex-1 cursor-pointer">
                              <div className="font-medium">Par séniorité</div>
                              <div className="text-sm text-muted-foreground">
                                Les points varient selon le niveau de séniorité
                              </div>
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {editedPolicy.attribution_model === 'uniform' && (
                        <div>
                          <Label htmlFor="monthlyCredit">Points mensuels par employé</Label>
                          <Input
                            id="monthlyCredit"
                            type="number"
                            value={editedPolicy.monthly_points_credit || 200}
                            onChange={(e) => setEditedPolicy({ ...editedPolicy, monthly_points_credit: parseInt(e.target.value) })}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Chaque employé recevra ce montant automatiquement chaque mois
                          </p>
                        </div>
                      )}

                      <div>
                        <Label htmlFor="basePointsPerWeek">Coût de base par semaine</Label>
                        <Input
                          id="basePointsPerWeek"
                          type="number"
                          value={editedPolicy.base_points_per_week || 100}
                          onChange={(e) => setEditedPolicy({ ...editedPolicy, base_points_per_week: parseInt(e.target.value) })}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Nombre de Nest Points nécessaires pour 1 semaine de travail à distance
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="maxBalance">Solde maximum</Label>
                        <Input
                          id="maxBalance"
                          type="number"
                          value={editedPolicy.maximum_balance || 1000}
                          onChange={(e) => setEditedPolicy({ ...editedPolicy, maximum_balance: parseInt(e.target.value) })}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Les employés ne peuvent pas accumuler plus que ce montant
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsPolicyDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button onClick={handleSavePolicy}>
                        Sauvegarder
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <span className="font-medium">Modèle</span>
                  </div>
                  <p className="text-2xl font-bold capitalize">{policy.attribution_model === 'uniform' ? 'Uniforme' : 'Par séniorité'}</p>
                  <p className="text-xs text-muted-foreground">Mode d'attribution des points</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Coins className="h-4 w-4 text-blue-500" />
                    <span className="font-medium">Coût par semaine</span>
                  </div>
                  <p className="text-2xl font-bold">{policy.base_points_per_week} pts</p>
                  <p className="text-xs text-muted-foreground">Base pour 1 semaine de workation</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-green-500" />
                    <span className="font-medium">Solde maximum</span>
                  </div>
                  <p className="text-2xl font-bold">{policy.maximum_balance} pts</p>
                  <p className="text-xs text-muted-foreground">≈ {weeksAffordable} semaines max</p>
                </div>
              </div>

              {policy.attribution_model === 'uniform' && (
                <div className="mt-6 p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Attribution mensuelle uniforme</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        Chaque employé reçoit <NestPointsDisplay points={policy.monthly_points_credit} showIcon={false} size="sm" className="inline-flex" /> par mois
                      </p>
                    </div>
                    <Badge>{policy.monthly_points_credit} pts/mois</Badge>
                  </div>
                </div>
              )}

              {policy.attribution_model === 'by_seniority' && seniorityLevels.length > 0 && (
                <div className="mt-6 p-4 bg-muted rounded-lg space-y-3">
                  <p className="font-medium">Attribution par niveau de séniorité</p>
                  {seniorityLevels.map(level => (
                    <div key={level.id} className="flex items-center justify-between">
                      <span className="text-sm">{level.level_name}</span>
                      <Badge>{level.monthly_points_credit} pts/mois</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {policy.attribution_model === 'by_seniority' && (
            <Card>
              <CardHeader>
                <CardTitle>Niveaux de séniorité</CardTitle>
                <CardDescription>Configurez les points mensuels par niveau</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {seniorityLevels.map((level, index) => (
                    <div key={level.id} className="flex items-center gap-4 p-4 border rounded-lg">
                      <Input
                        value={level.level_name}
                        onChange={(e) => {
                          const updated = [...seniorityLevels];
                          updated[index].level_name = e.target.value;
                          setSeniorityLevels(updated);
                        }}
                        placeholder="Nom du niveau"
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        value={level.monthly_points_credit}
                        onChange={(e) => {
                          const updated = [...seniorityLevels];
                          updated[index].monthly_points_credit = parseInt(e.target.value);
                          setSeniorityLevels(updated);
                        }}
                        placeholder="Points/mois"
                        className="w-32"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSaveSeniorityLevel(level)}
                      >
                        Sauvegarder
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSeniorityLevel(level.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    onClick={() => {
                      const newLevel: SeniorityLevel = {
                        id: '',
                        company_id: company!.id,
                        level_name: '',
                        monthly_points_credit: 200,
                        sort_order: seniorityLevels.length
                      };
                      setSeniorityLevels([...seniorityLevels, newLevel]);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un niveau
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="economics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5" />
                Économie des Nest Points
              </CardTitle>
              <CardDescription>
                Visualisez l'impact de votre politique sur les capacités de workation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 rounded-lg">
                  <h3 className="font-semibold text-lg mb-4">Calculateur de workation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Avec {policy.monthly_points_credit} pts/mois</p>
                      <p className="text-3xl font-bold">{Math.floor(policy.monthly_points_credit / policy.base_points_per_week)} semaines</p>
                      <p className="text-sm text-muted-foreground mt-1">par mois possible (même fuseau horaire)</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Avec solde maximum</p>
                      <p className="text-3xl font-bold">{weeksAffordable} semaines</p>
                      <p className="text-sm text-muted-foreground mt-1">maximum accumulables</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-3">Exemples de coûts</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Durée</TableHead>
                        <TableHead>Même fuseau</TableHead>
                        <TableHead>±2h</TableHead>
                        <TableHead>±4h</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">1 semaine</TableCell>
                        <TableCell>{policy.base_points_per_week} pts</TableCell>
                        <TableCell>{Math.round(policy.base_points_per_week * 1.5)} pts</TableCell>
                        <TableCell>{Math.round(policy.base_points_per_week * 2)} pts</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">2 semaines</TableCell>
                        <TableCell>{policy.base_points_per_week * 2} pts</TableCell>
                        <TableCell>{Math.round(policy.base_points_per_week * 3)} pts</TableCell>
                        <TableCell>{Math.round(policy.base_points_per_week * 4)} pts</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">1 mois (4 sem.)</TableCell>
                        <TableCell>{policy.base_points_per_week * 4} pts</TableCell>
                        <TableCell>{Math.round(policy.base_points_per_week * 6)} pts</TableCell>
                        <TableCell>{Math.round(policy.base_points_per_week * 8)} pts</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Gestion des points employés
                  </CardTitle>
                  <CardDescription>
                    Soldes actuels et attribution de bonus
                  </CardDescription>
                </div>
                <Dialog open={isAddPointsDialogOpen} onOpenChange={setIsAddPointsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter bonus
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Ajouter des points bonus</DialogTitle>
                      <DialogDescription>
                        Récompensez un employé avec des Nest Points supplémentaires
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="employee">Employé</Label>
                        <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un employé" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map(employee => (
                              <SelectItem key={employee.id} value={employee.id}>
                                {employee.first_name} {employee.last_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="points">Nombre de points</Label>
                        <Input
                          id="points"
                          type="number"
                          value={pointsToAdd}
                          onChange={(e) => setPointsToAdd(e.target.value)}
                          placeholder="100"
                        />
                      </div>
                      <div>
                        <Label htmlFor="reason">Raison (optionnel)</Label>
                        <Input
                          id="reason"
                          value={bonusReason}
                          onChange={(e) => setBonusReason(e.target.value)}
                          placeholder="Performance exceptionnelle Q4"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddPointsDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button onClick={handleAddBonusPoints}>
                        Ajouter
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {employeePoints.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>Aucun point attribué ce mois-ci</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employé</TableHead>
                      <TableHead>Solde total</TableHead>
                      <TableHead>Points utilisés</TableHead>
                      <TableHead>Disponible</TableHead>
                      <TableHead>% du maximum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employeePoints.map(points => {
                      const available = points.total_points - points.used_points;
                      const percentage = Math.round((points.total_points / policy.maximum_balance) * 100);
                      const getColorClass = () => {
                        if (percentage > 70) return "text-green-600 dark:text-green-400";
                        if (percentage >= 30) return "text-yellow-600 dark:text-yellow-400";
                        return "text-red-600 dark:text-red-400";
                      };
                      const getBarColor = () => {
                        if (percentage > 70) return "bg-green-500";
                        if (percentage >= 30) return "bg-yellow-500";
                        return "bg-red-500";
                      };
                      return (
                        <TableRow key={points.id}>
                          <TableCell className="font-medium">{points.employee_name}</TableCell>
                          <TableCell>
                            <NestPointsDisplay points={points.total_points} size="sm" />
                          </TableCell>
                          <TableCell>
                            <NestPointsDisplay points={points.used_points} size="sm" />
                          </TableCell>
                          <TableCell>
                            <span className={`font-semibold ${getColorClass()}`}>
                              {available.toLocaleString()} pts
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full transition-all ${getBarColor()}`}
                                  style={{ width: `${Math.min(percentage, 100)}%` }}
                                />
                              </div>
                              <span className={`text-sm w-12 text-right font-semibold ${getColorClass()}`}>
                                {percentage}%
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="advanced" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Restrictions de fuseau horaire
              </CardTitle>
              <CardDescription>
                Limitez les destinations selon le décalage horaire depuis votre siège
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium">Activer les restrictions de fuseau</p>
                  <p className="text-sm text-muted-foreground">
                    Empêcher les réservations au-delà d'un certain décalage horaire
                  </p>
                </div>
                <Switch
                  checked={editedTimezone.enforce_timezone_restrictions || false}
                  onCheckedChange={(checked) => {
                    setEditedTimezone({ ...editedTimezone, enforce_timezone_restrictions: checked });
                    if (timezonePolicy) {
                      supabase
                        .from('company_timezone_policy')
                        .update({ enforce_timezone_restrictions: checked })
                        .eq('id', timezonePolicy.id);
                    }
                  }}
                />
              </div>

              {editedTimezone.enforce_timezone_restrictions && (
                <div className="space-y-4">
                  <div>
                    <Label>Décalage horaire maximum autorisé: ±{editedTimezone.max_timezone_difference_hours || 4}h</Label>
                    <Slider
                      value={[editedTimezone.max_timezone_difference_hours || 4]}
                      onValueChange={([value]) => setEditedTimezone({ ...editedTimezone, max_timezone_difference_hours: value })}
                      max={12}
                      min={0}
                      step={1}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Les employés ne pourront choisir que des destinations dans ce fuseau horaire
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="hqTimezone">Fuseau horaire du siège</Label>
                    <Input
                      id="hqTimezone"
                      value={editedTimezone.company_headquarters_timezone || 'UTC+1'}
                      onChange={(e) => setEditedTimezone({ ...editedTimezone, company_headquarters_timezone: e.target.value })}
                      placeholder="UTC+1"
                    />
                  </div>

                  <Button onClick={() => {
                    if (timezonePolicy) {
                      supabase
                        .from('company_timezone_policy')
                        .update(editedTimezone)
                        .eq('id', timezonePolicy.id)
                        .then(() => {
                          toast({
                            title: "Restrictions sauvegardées",
                            description: "Les restrictions de fuseau horaire ont été mises à jour"
                          });
                          fetchAllData();
                        });
                    }
                  }}>
                    Sauvegarder les restrictions
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Tarification dynamique par distance
              </CardTitle>
              <CardDescription>
                Ajustez le coût en points selon le décalage horaire
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[0, 1, 2, 3, 4, 5, 6].map(tzDiff => {
                  const existing = distancePricing.find(d => d.timezone_difference_hours === tzDiff);
                  const multiplier = existing?.points_per_week_multiplier || (tzDiff === 0 ? 1.0 : tzDiff <= 2 ? 1.5 : tzDiff <= 4 ? 2.0 : 2.5);
                  const category = existing?.distance_category || (tzDiff === 0 ? 'same_timezone' : tzDiff <= 2 ? 'near' : tzDiff <= 4 ? 'medium' : 'far');
                  
                  return (
                    <div key={tzDiff} className="flex items-center gap-4 p-4 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">±{tzDiff}h de décalage</p>
                        <p className="text-sm text-muted-foreground">{category}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          value={multiplier}
                          onChange={(e) => {
                            const newMultiplier = parseFloat(e.target.value);
                            handleSaveDistancePricing(tzDiff, newMultiplier, category);
                          }}
                          className="w-20"
                        />
                        <span className="text-sm text-muted-foreground">× base</span>
                      </div>
                      <Badge variant="outline">{Math.round(policy.base_points_per_week * multiplier)} pts/sem</Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GovernanceTab;
